import {useCallback, useMemo} from 'react'

import {findByFieldName} from '../../../../fields'
import {QuantitySelection} from '../../../../fields/quantity/types'
import {useOrderProcessFormContext} from '../../../../form/useOrderProcessFormContext'
import {
  BuildSlotRequestResult,
  SLOT_MANAGEMENT_DEPENDENT_FIELDS,
  useSlotManagementContext,
  useSlotRequest
} from '../../../../slotManagement'
import {useChangeableFields} from '../../changeableFields'
import {resolveVariationCodeSlotReferenceDateTime} from '../resolveVariationCodeSlotReferenceDateTime'
import {isOnlyMixChanged} from './isOnlyMixChanged'

import {isQuantityOnlyDecreaseOnConfirmedOrder} from './isQuantityOnlyDecreaseOnConfirmedOrder'

/**
 * Result of validation enablement check
 */
export interface SlotValidationEnabledResult {
  /** Whether validation should run */
  isEnabled: boolean
  /** Whether request is already autoConfirmable */
  isAutoConfirmable: boolean
  /** Slot request build result (params, validity, missing fields) */
  slotRequestResult: BuildSlotRequestResult
}

/**
 * Hook to determine if Order Change slot validation should be enabled.
 *
 * Validation is enabled when:
 * - Feature toggle is on
 * - Order is NOT auto-confirmable (requires dispatch approval)
 * - At least one slot-managed field is dirty
 * - Slot request params can be built successfully
 *
 * This logic is shared between query-based and mutation-based validation.
 *
 * Uses 'orderChangeRulesOnly' mode to check fields without slot management rules applied.
 * This prevents circular logic where slot validation depends on slot rules.
 *
 * @returns Enablement status and slot request result
 */
export const useOrderChangeSlotValidationEnabled = (): SlotValidationEnabledResult => {
  // Get config from slot management context
  const {config} = useSlotManagementContext()

  // Access form context for quantity comparison (current vs original)
  const {watch, formState} = useOrderProcessFormContext()
  const currentQuantity = watch('quantity') as QuantitySelection | undefined
  const originalQuantity = formState.defaultValues?.quantity as QuantitySelection | undefined

  // Use orderChangeRulesOnly mode - fields with Order Change rules but without slot management
  // This gives us clean intermediate state for validation logic
  const {changeableFields, confirmationState} = useChangeableFields('orderChangeRulesOnly')

  // Check if any slot-managed fields are dirty and require manual approval
  const slotManagementDependentFields = SLOT_MANAGEMENT_DEPENDENT_FIELDS.map((fieldName) =>
    findByFieldName(changeableFields, fieldName)
  )

  const hasSlotManagedFieldsRequireManualApproval = slotManagementDependentFields.some(
    (field) => field && field.isDirty && field.state === 'requireDispatchConfirmation'
  )

  // isAutoConfirmable from orderChangeRulesOnly confirmation state
  const isAutoConfirmable = confirmationState === 'autoConfirmable'

  // HCP-108022: Skip slot validation when quantity is decreased on a confirmed order
  // and no other slot-managed fields are changed. This allows auto-approval without
  // calling slot management since decreasing quantity cannot exceed capacity.
  const isQuantityDecreaseOnConfirmed = useMemo(
    () =>
      isQuantityOnlyDecreaseOnConfirmedOrder({
        isOrderConfirmedByCustomer: config.isOrderConfirmedByCustomer ?? false,
        changeableFields,
        currentQuantity,
        originalQuantity
      }),
    [config.isOrderConfirmedByCustomer, changeableFields, currentQuantity, originalQuantity]
  )

  const resolveDateTime = useCallback(
    (currentDateTime: string) =>
      resolveVariationCodeSlotReferenceDateTime({
        currentDateTime,
        originalDateTime: config.originalDateTimeForSlotReference,
        variationCode: config.variationCode,
        variationCodeOffset: config.variationCodeOffset,
        variationCodeAdjustedTime: config.variationCodeAdjustedTime
      }),
    [
      config.originalDateTimeForSlotReference,
      config.variationCode,
      config.variationCodeAdjustedTime,
      config.variationCodeOffset
    ]
  )

  // Build slot request parameters
  const slotRequestResult: BuildSlotRequestResult = useSlotRequest({
    orgUnitId: config.orgUnitId,
    siteNumber: config.siteNumber,
    orderId: config.orderId,
    contractNumber: config.contractNumber,
    contractItemPositionNumber: config.contractItemPositionNumber,
    dateIndex: 0,
    normalizeDate: false, // Validation endpoint needs exact time
    resolveDateTime
  })

  // HCP-108663: when the mix (material and/or slump / setControl) is the ONLY changed slot-managed
  // field, ask the backend to short-circuit the OTV call if the resolved plant / dispatch group did
  // not change. The flag is validate-only and is attached to the request params below.
  const checkOnlyIfLogisticsChanged = useMemo(
    () => isOnlyMixChanged({changeableFields}),
    [changeableFields]
  )

  // Attach the flag to the built params only when material is the sole change (omit otherwise).
  const enrichedSlotRequestResult: BuildSlotRequestResult = useMemo(() => {
    if (!slotRequestResult.isValid || !checkOnlyIfLogisticsChanged) {
      return slotRequestResult
    }
    return {
      ...slotRequestResult,
      params: {...slotRequestResult.params, checkOnlyIfLogisticsChanged: true}
    }
  }, [slotRequestResult, checkOnlyIfLogisticsChanged])

  const isEnabled = useMemo(
    () =>
      config.isFeatureEnabled &&
      !isAutoConfirmable &&
      !isQuantityDecreaseOnConfirmed &&
      hasSlotManagedFieldsRequireManualApproval &&
      slotRequestResult.isValid,
    [
      config.isFeatureEnabled,
      isAutoConfirmable,
      isQuantityDecreaseOnConfirmed,
      hasSlotManagedFieldsRequireManualApproval,
      slotRequestResult.isValid
    ]
  )

  // Warn if params couldn't be built but we should validate
  if (
    config.isFeatureEnabled &&
    !isAutoConfirmable &&
    !isQuantityDecreaseOnConfirmed &&
    hasSlotManagedFieldsRequireManualApproval &&
    !slotRequestResult.isValid
  ) {
    console.warn(
      '[useOrderChangeSlotValidationEnabled] Could not build slot request params. Missing fields:',
      slotRequestResult.missingFields
    )
  }

  return {isEnabled, isAutoConfirmable, slotRequestResult: enrichedSlotRequestResult}
}
